<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";
import PrimaryButton from "@/components/shared/buttons/PrimaryButton.vue";
import SecondaryButton from "@/components/shared/buttons/SecondaryButton.vue";
import TooltipButton from "@/components/shared/buttons/TooltipButton.vue";
import type { ButtonSize, ButtonTone } from "@/components/shared/buttons/types";

type DialogActionsAlign = "start" | "center" | "end" | "between";
type DialogActionsDirection = "row" | "column" | "responsive";
type DialogAction = () => void | undefined | Promise<void | undefined>;

type DialogClassValue = string | Record<string, boolean> | DialogClassValue[];

export type DialogActionButton = {
  text?: string;
  /** Contextual glyph shown before the label (e.g. `mdi-delete-outline`). */
  icon?: string;
  tooltip?: string;
  disabledTooltip?: string;
  action?: DialogAction;
  show?: boolean;
  tone?: ButtonTone;
  disabled?: boolean;
  loading?: boolean;
  class?: string;
};

type ResolvedDialogActionButton = {
  text: string;
  icon?: string;
  tooltip?: string;
  disabledTooltip?: string;
  action?: DialogAction;
  show: boolean;
  tone: ButtonTone;
  disabled: boolean;
  loading: boolean;
  class: string;
};

const props = withDefaults(
  defineProps<{
    secondary?: DialogActionButton;
    primary?: DialogActionButton;

    loading?: boolean;
    disabled?: boolean;

    align?: DialogActionsAlign;
    direction?: DialogActionsDirection;
    fullWidthButtonsOnMobile?: boolean;

    buttonSize?: ButtonSize;
    mobileButtonSize?: ButtonSize;

    actionsClass?: DialogClassValue;
    mobileActionsClass?: DialogClassValue;
    buttonClass?: DialogClassValue;
  }>(),
  {
    loading: false,
    disabled: false,

    align: "center",
    direction: "responsive",
    fullWidthButtonsOnMobile: true,

    buttonSize: "large",
    mobileButtonSize: "large",

    actionsClass: "",
    mobileActionsClass: "",
    buttonClass: "",
  }
);

const emit = defineEmits<{
  secondary: [];
  primary: [];
}>();

const { t } = useI18n();
const { smAndDown } = useDisplay();

const isMobile = computed(() => smAndDown.value);

const localSecondaryLoading = ref(false);
const localPrimaryLoading = ref(false);

const secondaryButton = computed<ResolvedDialogActionButton>(() => ({
  text: props.secondary?.text ?? t("generic.action.cancel"),
  /* Pairs with the `cancel`/`confirm` text defaults above: a dialog that leaves
     both unset gets the dismiss/confirm glyphs. Any caller whose slot holds a
     different action (save, discard, decline) passes its own `icon`. */
  icon: props.secondary?.icon ?? "mdi-close",
  tooltip: props.secondary?.tooltip,
  disabledTooltip: props.secondary?.disabledTooltip,
  action: props.secondary?.action,
  show: props.secondary?.show ?? !!props.secondary,
  tone: props.secondary?.tone ?? "brand",
  disabled: props.secondary?.disabled ?? false,
  loading: props.secondary?.loading ?? false,
  class: props.secondary?.class ?? "",
}));

const primaryButton = computed<ResolvedDialogActionButton>(() => ({
  text: props.primary?.text ?? t("generic.action.confirm"),
  icon: props.primary?.icon ?? "mdi-check",
  tooltip: props.primary?.tooltip,
  disabledTooltip: props.primary?.disabledTooltip,
  action: props.primary?.action,
  show: props.primary?.show ?? !!props.primary,
  tone: props.primary?.tone ?? "brand",
  disabled: props.primary?.disabled ?? false,
  loading: props.primary?.loading ?? false,
  class: props.primary?.class ?? "",
}));

const isSecondaryLoading = computed(
  () => props.loading || secondaryButton.value.loading || localSecondaryLoading.value
);

const isPrimaryLoading = computed(
  () => props.loading || primaryButton.value.loading || localPrimaryLoading.value
);

const isAnyLoading = computed(() => isSecondaryLoading.value || isPrimaryLoading.value);

const isSecondaryDisabled = computed(
  () => props.disabled || secondaryButton.value.disabled || isAnyLoading.value
);

const isPrimaryDisabled = computed(
  () => props.disabled || primaryButton.value.disabled || isAnyLoading.value
);

const isBlockButton = computed(() => props.fullWidthButtonsOnMobile && isMobile.value);

const hasSecondaryTooltip = computed(
  () => !!secondaryButton.value.tooltip || !!secondaryButton.value.disabledTooltip
);

const hasPrimaryTooltip = computed(
  () => !!primaryButton.value.tooltip || !!primaryButton.value.disabledTooltip
);

const resolvedButtonSize = computed(() => {
  return isMobile.value ? props.mobileButtonSize : props.buttonSize;
});

const secondaryButtonClass = computed(() => [
  props.buttonClass,
  secondaryButton.value.class,
  props.fullWidthButtonsOnMobile && isMobile.value ? "tw:w-full" : "",
]);

const primaryButtonClass = computed(() => [
  props.buttonClass,
  primaryButton.value.class,
  props.fullWidthButtonsOnMobile && isMobile.value ? "tw:w-full" : "",
]);

const alignClass = computed(() => {
  if (props.align === "start") return "tw:justify-start";
  if (props.align === "center") return "tw:justify-center";
  if (props.align === "between") return "tw:justify-between";

  return "tw:justify-end";
});

const directionClass = computed(() => {
  if (props.direction === "column") return "tw:flex-col";
  if (props.direction === "row") return "tw:flex-row";

  return isMobile.value ? "tw:flex-col" : "tw:flex-row";
});

const classes = computed(() => [
  "tw:flex tw:w-full tw:items-center tw:gap-3",
  alignClass.value,
  directionClass.value,
  isMobile.value ? props.mobileActionsClass : "",
  props.actionsClass,
]);

const handleSecondaryClick = async () => {
  if (isSecondaryDisabled.value) {
    return;
  }

  localSecondaryLoading.value = true;

  try {
    await secondaryButton.value.action?.();
    emit("secondary");
  } finally {
    localSecondaryLoading.value = false;
  }
};

const handlePrimaryClick = async () => {
  if (isPrimaryDisabled.value) {
    return;
  }

  localPrimaryLoading.value = true;

  try {
    await primaryButton.value.action?.();
    emit("primary");
  } finally {
    localPrimaryLoading.value = false;
  }
};
</script>

<template>
  <div :class="classes">
    <slot
      name="secondary"
      :loading="isSecondaryLoading"
      :disabled="isSecondaryDisabled"
      :click="handleSecondaryClick"
      :is-mobile="isMobile"
    >
      <TooltipButton
        v-if="secondaryButton.show && hasSecondaryTooltip"
        kind="secondary"
        :class="secondaryButtonClass"
        :size="resolvedButtonSize"
        :tone="secondaryButton.tone"
        :prepend-icon="secondaryButton.icon"
        :tooltip="secondaryButton.tooltip"
        :disabled-tooltip="secondaryButton.disabledTooltip"
        :block="isBlockButton"
        :loading="isSecondaryLoading"
        :disabled="isSecondaryDisabled"
        @click="handleSecondaryClick"
      >
        {{ secondaryButton.text }}
      </TooltipButton>
      <SecondaryButton
        v-else-if="secondaryButton.show"
        :class="secondaryButtonClass"
        :size="resolvedButtonSize"
        :tone="secondaryButton.tone"
        :prepend-icon="secondaryButton.icon"
        :loading="isSecondaryLoading"
        :disabled="isSecondaryDisabled"
        @click="handleSecondaryClick"
      >
        {{ secondaryButton.text }}
      </SecondaryButton>
    </slot>

    <slot
      name="primary"
      :loading="isPrimaryLoading"
      :disabled="isPrimaryDisabled"
      :click="handlePrimaryClick"
      :is-mobile="isMobile"
    >
      <TooltipButton
        v-if="primaryButton.show && hasPrimaryTooltip"
        kind="primary"
        :class="primaryButtonClass"
        :size="resolvedButtonSize"
        :tone="primaryButton.tone"
        :prepend-icon="primaryButton.icon"
        :tooltip="primaryButton.tooltip"
        :disabled-tooltip="primaryButton.disabledTooltip"
        :block="isBlockButton"
        :loading="isPrimaryLoading"
        :disabled="isPrimaryDisabled"
        @click="handlePrimaryClick"
      >
        {{ primaryButton.text }}
      </TooltipButton>
      <PrimaryButton
        v-else-if="primaryButton.show"
        :class="primaryButtonClass"
        :size="resolvedButtonSize"
        :tone="primaryButton.tone"
        :prepend-icon="primaryButton.icon"
        :loading="isPrimaryLoading"
        :disabled="isPrimaryDisabled"
        @click="handlePrimaryClick"
      >
        {{ primaryButton.text }}
      </PrimaryButton>
    </slot>
  </div>
</template>
